// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import React, { FC, useState } from "react";
import { css } from "styled-components";

import ProductActions, { ProductActionsStyle } from "@eisinc/App/Components/Product/ProductActions";
import ProductOptionsTableLink from "@eisinc/App/Components/Product/ProductOptionsTableLink";
import ProductQtyInfo, { ProductQtyInfoStyles } from "@eisinc/App/Components/Product/ProductQtyInfo";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@eisinc/Overrides/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@eisinc/Overrides/Components/ProductAvailability";
import { color, hideElementOnPrint, mergeWithCss, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { connect } from "react-redux";
import { XProductModel } from "@eisinc/Overrides/Pages/ProductDetailsPage";
import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";

const mapStateToProps = (state: ApplicationState) => ({
    configurationSelection: state.pages.productDetails.configurationSelection,
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    subscription: state.pages.productDetails.subscription,
});
export interface ProductDetailsActionsProps {
    extendedStyles?: ProductDetailsActionsBoxStyles;
}

type Props = ProductDetailsActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsActionsBoxStyles {
    wrapper?: InjectableCss;
    productPriceAvailabilityWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productActionsStyles?: ProductActionsStyle;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productQtyInfoStyles?: ProductQtyInfoStyles;
    productInfoSkeletonWrapper?: InjectableCss;
}

export const productDetailsActionsBox: ProductDetailsActionsBoxStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
        `,
    },

    productPriceAvailabilityWrapper: {
        css: css`
            > div:not(:last-child) {
                margin-bottom: 8px;
            }
        `,
    },

    actionsWrapper: {
        css: css`
            display: flex;
            gap: 12px 24px;
            align-items: baseline;
            flex-wrap: wrap;
        `,
    },

    productActionsStyles: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 8px;
                flex-wrap: nowrap;
                width: auto;
                height: 32px;

                ${xScreen("lte", "md")} {
                    width: 100%;
                    height: auto;
                    flex-wrap: wrap;
                    &:has(> :nth-child(3)) > :nth-child(3) {
                        flex-basis: 100%;
                    }
                }

                ${hideElementOnPrint}
            `,
        },

        qtyWrapper: {
            css: css`
                width: auto;
                & > div {
                    height: 34px;
                    align-items: center;
                }
            `,
        },
        uomSelectWrapper: {
            css: css`
                flex: 0 0 auto;
                width: 130px;

                ${xScreen("lte", "md")} {
                    flex: 1 1 auto;
                    width: auto;
                    min-width: 0;
                }
            `,
        },

        addToCartButtonWrapper: {
            css: css`
                flex: 0 0 auto;
                min-width: 0;

                ${xScreen("lte", "md")} {
                    flex: 1 1 auto;
                }
            `,
        },

        productAddToCartButtonStyles: {
            sizeVariant: "medium",
            css: css`
                width: 126px;
                ${xScreen("lte", "md")} {
                    width: 100%;
                }
            `,
        },

        uomSelect: { sizeVariant: "default" },
    },
    productInfoSkeletonWrapper: {
        css: css`
            padding-top: 12px;
            border-top: 1px solid ${color("common.border")};
        `,
    },
};

const ProductDetailsActions: FC<Props> = ({
    extendedStyles,
    productContext: { product },
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
}) => {
    const [styles] = useState(() => mergeWithCss(productDetailsActionsBox, extendedStyles));
    const isLoading = (product as XProductModel)?.isLoading;
    if (isLoading) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Skeleton height="34px" width="30%" />
                <Skeleton height="35px" />
                <Skeleton height="52px" />
                <StyledWrapper {...styles.productInfoSkeletonWrapper}>
                    <Skeleton height="17px" width="320px" />
                </StyledWrapper>
                <Skeleton height="32px" width="230px" />
            </StyledWrapper>
        );
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...(styles.productPriceAvailabilityWrapper ?? {})}>
                {createWidgetElement("ProductDetails/ProductDetailsPrice", {
                    fields: {},
                })}
                <ProductContextAvailability
                    extendedStyles={styles.productAvailabilityStyles}
                    isProductDetailsPage={true}
                />
            </StyledWrapper>
            {(product?.isVariantParent || (product?.variantTraits?.length ?? 0) > 0) && <ProductOptionsTableLink />}

            {createWidgetElement("ProductDetails/ProductDetailsVariantOptions", {
                fields: {},
            })}
            <ProductQtyInfo extendedStyles={styles.productQtyInfoStyles} />
            <StyledWrapper {...styles.actionsWrapper}>
                {/* todo : remove isLoading? I think we can remove it */}
                <ProductActions
                    configurationSelection={configurationSelection}
                    configurationCompleted={configurationCompleted}
                    variantSelectionCompleted={variantSelectionCompleted}
                    subscription={subscription}
                    isPDP={true}
                    {...{ isLoading }}
                    extendedStyles={styles.productActionsStyles}
                />
                <ProductAddToListLink
                    data-test-selector="addToListLink"
                    showIcon={true}
                    iconSize={14}
                    extendedStyles={styles.productAddToListLinkStyles}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProductContext(ProductDetailsActions)),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
